import React from 'react';
import {
  AlertTriangle,
  ArrowUp,
  Calendar,
  ChevronRight,
  Clock,
  CreditCard,
  FileText,
  HelpCircle,
  Loader2,
  Package,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  Wallet,
  X,
} from 'lucide-react';
import { useNavigation } from '@/shared/hooks/useNavigation';
import {
  BalanceHistoryEntry,
  OrganizationBalanceClientRepository,
} from '@/services/firebase/organization-balance-repository';
import SeoTags from '@/components/shared/SeoTags';

const formatCurrency = (valueInCents: number | null | undefined) => {
  if (valueInCents === null || valueInCents === undefined || Number.isNaN(valueInCents)) {
    return '—';
  }
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  }).format(valueInCents / 100);
};

const formatDate = (d?: Date) => {
  if (!d) return '—';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(d);
};

const formatTime = (d?: Date) => {
  if (!d) return '';
  return new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
};

const formatDelta = (delta: number) => {
  const sign = delta > 0 ? '+' : '';
  return `${sign}${formatCurrency(delta)}`;
};

const reasonLabels: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  shipping_balance_adjustment: {
    label: 'Ajustement livraison',
    icon: Package,
    color: 'text-blue-600 bg-blue-50',
  },
  shipping_balance_delta_on_payment: {
    label: 'Écart frais de port',
    icon: Package,
    color: 'text-indigo-600 bg-indigo-50',
  },
  shipping_debt_refund: {
    label: 'Dette livraison (remboursement)',
    icon: RefreshCw,
    color: 'text-orange-600 bg-orange-50',
  },
  shipping_debt_cancelled: {
    label: 'Annulation dette livraison',
    icon: RefreshCw,
    color: 'text-emerald-600 bg-emerald-50',
  },
  commission_debt_refund: {
    label: 'Commission conservée',
    icon: CreditCard,
    color: 'text-purple-600 bg-purple-50',
  },
  refund_shipping_debt: {
    label: 'Dette livraison',
    icon: Package,
    color: 'text-amber-600 bg-amber-50',
  },
};

const metadataLabels: Record<string, string> = {
  shippingCustomPrice: 'Frais de port facturés',
  shippingActualPrice: 'Coût réel transporteur',
  shippingDifference: 'Différence',
  shippingDeficit: 'Déficit à récupérer',
  applicationFeeAmount: 'Commission plateforme',
  sendcloudRefundSuccess: 'Remboursement Sendcloud',
  shippingCost: 'Frais de livraison',
  previousParcelWeightGrams: 'Poids déclaré (g)',
  realParcelWeightGrams: 'Poids facturé (g)',
  parcelWeightDeltaGrams: 'Écart de poids (g)',
  adjustmentReason: 'Raison',
};

const formatKgFromGrams = (g: number) =>
  `${(g / 1000).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg`;

const getReasonDisplay = (reason?: string) => {
  if (!reason) return { label: 'Ajustement', icon: FileText, color: 'text-slate-600 bg-slate-50' };
  return reasonLabels[reason] || { label: reason, icon: FileText, color: 'text-slate-600 bg-slate-50' };
};

export const BalanceHistoryPage: React.FC = () => {
  const { organizationId } = useNavigation();
  const [balance, setBalance] = React.useState<number | null>(null);
  const [history, setHistory] = React.useState<BalanceHistoryEntry[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [expandedRow, setExpandedRow] = React.useState<string | null>(null);
  const [showInfoCard, setShowInfoCard] = React.useState(true);

  React.useEffect(() => {
    if (!organizationId) {
      setError('Aucune organisation sélectionnée.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubBalance = OrganizationBalanceClientRepository.listenToCurrentBalance(
      organizationId,
      (b) => setBalance(b)
    );

    const unsubHistory = OrganizationBalanceClientRepository.listenToHistory(
      organizationId,
      (entries) => {
        setHistory(entries);
        setLoading(false);
      }
    );

    return () => {
      unsubBalance?.();
      unsubHistory?.();
    };
  }, [organizationId]);

  // Calculate stats
  const totalDebits = history.filter((e) => e.delta > 0).reduce((acc, e) => acc + e.delta, 0);

  const isDebt = balance !== null && balance < 0;
  const isCredit = balance !== null && balance > 0;

  return (
    <div className="min-h-screen pt-4">
      <SeoTags
        title={`Solde | CashLive`}
        description={`Historique des ajustements financiers de l’organisation`}
        url={typeof window !== 'undefined' ? window.location.href : ''}
        noIndex={true}
      />
      {/* Info Card */}
      {showInfoCard && (
        <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-4 relative">
          <button
            type="button"
            onClick={() => setShowInfoCard(false)}
            className="absolute top-3 right-3 p-1 rounded-lg text-blue-400 hover:text-blue-600 hover:bg-blue-100 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex gap-3">
            <div className="p-2 rounded-lg bg-blue-100 text-blue-600 h-fit">
              <HelpCircle className="h-5 w-5" />
            </div>
            <div className="pr-6">
              <h3 className="font-semibold text-slate-800 mb-1">
                À quoi sert cette page ?
              </h3>
              <p className="text-sm text-slate-600 mb-3">
                Cette page affiche le <strong>solde de votre organisation</strong> et l'historique de tous les ajustements financiers.
              </p>
              <div className="space-y-2 text-sm text-slate-600">
                <div className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0" />
                  <p>
                    <strong className="text-slate-700">Écart frais de port :</strong> Si le prix de livraison configuré pour vos clients est inférieur au coût réel du transporteur, la différence est enregistrée ici et sera prélevée sur vos prochains paiements.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-1.5 shrink-0" />
                  <p>
                    <strong className="text-slate-700">Remboursements :</strong> Lors d'un remboursement, si le label de livraison ne peut pas être annulé (déjà expédié ou délai dépassé), les frais sont ajoutés à votre solde.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                  <p>
                    <strong className="text-slate-700">Récupération automatique :</strong> Les montants dus sont automatiquement prélevés sur vos prochaines ventes jusqu'à équilibre du solde.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compact Balance Header */}
      <div className="relative overflow-hidden rounded-2xl mb-6 shadow-xl">
        <div
          className={`absolute inset-0 ${isDebt
            ? 'bg-gradient-to-br from-amber-500 via-orange-500 to-rose-600'
            : isCredit
              ? 'bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600'
              : 'bg-gradient-to-br from-slate-600 via-slate-700 to-slate-800'
            }`}
        />
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-white/5 blur-2xl" />

        <div className="relative px-5 sm:px-8 py-6 sm:py-8 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div className="flex items-center gap-4 sm:gap-6">
            <div className="p-3.5 rounded-2xl bg-white/20 backdrop-blur-md shadow-lg flex-shrink-0">
              <Wallet className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-white/80 text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-1.5">
                Solde actuel
              </p>
              <div className="flex items-center gap-3">
                <span className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight tabular-nums">
                  {formatCurrency(balance ?? null)}
                </span>
                <div className="flex flex-col gap-1">
                  {isDebt && (
                    <span className="px-2.5 py-1 rounded-lg bg-white/20 text-white text-[10px] sm:text-xs font-bold uppercase tracking-wider backdrop-blur-md">
                      Dette
                    </span>
                  )}
                  {isCredit && (
                    <span className="px-2.5 py-1 rounded-lg bg-white/20 text-white text-[10px] sm:text-xs font-bold uppercase tracking-wider backdrop-blur-md">
                      Crédit
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2.5 text-white/90 text-xs sm:text-sm font-medium bg-white/10 backdrop-blur-md px-4 py-2.5 rounded-xl border border-white/10 self-start sm:self-center">
            {isDebt ? (
              <>
                <TrendingDown className="h-4 w-4" />
                <span>Prélevé sur les prochains paiements</span>
              </>
            ) : isCredit ? (
              <>
                <TrendingUp className="h-4 w-4" />
                <span>À rembourser</span>
              </>
            ) : (
              <span>Aucun mouvement en attente</span>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div className="group relative bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-all duration-200">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 rounded-lg bg-amber-50 text-amber-600">
              <ArrowUp className="h-4 w-4" />
            </div>
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">
              Total dettes
            </span>
          </div>
          <p className="text-xl font-bold text-slate-900">{formatCurrency(totalDebits)}</p>
          <p className="text-xs text-slate-500 mt-1">Montants à récupérer cumulés</p>
        </div>

        <div className="group relative bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-all duration-200">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 rounded-lg bg-violet-50 text-violet-600">
              <Clock className="h-4 w-4" />
            </div>
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">
              Mouvements
            </span>
          </div>
          <p className="text-xl font-bold text-slate-900">{history.length}</p>
          <p className="text-xs text-slate-500 mt-1">
            {history[0]?.createdAt
              ? `Dernier: ${formatDate(history[0].createdAt)}`
              : 'Aucun mouvement'}
          </p>
        </div>
      </div>

      {/* History Timeline */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-100">
                <Calendar className="h-4 w-4 text-slate-600" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900">Historique détaillé</h2>
                <p className="text-xs text-slate-500">Tous les ajustements de solde</p>
              </div>
            </div>
            {loading && (
              <div className="flex items-center gap-2 text-sm text-slate-500 bg-slate-100 px-3 py-1.5 rounded-full">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Chargement…
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="p-4 bg-rose-50 border-b border-rose-100 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-rose-100">
              <AlertTriangle className="h-4 w-4 text-rose-600" />
            </div>
            <p className="text-sm text-rose-700">{error}</p>
          </div>
        )}

        {!loading && !error && history.length === 0 && (
          <div className="p-12 text-center">
            <div className="inline-flex p-4 rounded-full bg-slate-100 mb-4">
              <FileText className="h-8 w-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-1">Aucun mouvement</h3>
            <p className="text-sm text-slate-500">
              L'historique des ajustements apparaîtra ici
            </p>
          </div>
        )}

        {history.length > 0 && (
          <div className="divide-y divide-slate-100">
            {history.map((entry, index) => {
              const isExpanded = expandedRow === entry.id;
              const reasonDisplay = getReasonDisplay(entry.reason);
              const ReasonIcon = reasonDisplay.icon;
              const isPositive = entry.delta > 0;

              return (
                <div
                  key={entry.id}
                  className="group hover:bg-slate-50/50 transition-colors"
                >
                  <button
                    type="button"
                    onClick={() => setExpandedRow(isExpanded ? null : entry.id ?? null)}
                    className="w-full px-4 sm:px-6 py-5 flex items-start sm:items-center gap-3 sm:gap-4 text-left group-hover:bg-slate-50 transition-colors"
                  >
                    {/* Timeline indicator */}
                    <div className="flex flex-col items-center self-stretch pt-1 sm:pt-0">
                      <div
                        className={`w-3 h-3 rounded-full ring-4 ${isPositive
                          ? 'bg-amber-500 ring-amber-100'
                          : 'bg-emerald-500 ring-emerald-100'
                          }`}
                      />
                      {index < history.length - 1 && (
                        <div className="w-0.5 flex-1 bg-slate-200 my-2" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1.5">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-wider ${reasonDisplay.color}`}
                        >
                          <ReasonIcon className="h-3.5 w-3.5" />
                          {reasonDisplay.label}
                        </span>
                        {entry.orderId && (
                          <span className="text-xs text-slate-400 font-mono bg-slate-100 px-1.5 py-0.5 rounded">
                            #{entry.orderId.slice(-8)}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500 font-medium">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(entry.createdAt)} {formatTime(entry.createdAt)}
                        </span>
                        {entry.actor?.type && (
                          <>
                            <span className="text-slate-300">•</span>
                            <span className="capitalize px-1.5 py-0.5 bg-slate-100 rounded text-[10px]">{entry.actor.type}</span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Amount & Expand wrapper for mobile */}
                    <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 sm:gap-4">
                      <div className="text-right">
                        <p
                          className={`text-lg sm:text-xl font-bold tabular-nums ${isPositive ? 'text-amber-600' : 'text-emerald-600'
                            }`}
                        >
                          {formatDelta(entry.delta)}
                        </p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
                          → {formatCurrency(entry.balanceAfter)}
                        </p>
                      </div>

                      {/* Expand indicator */}
                      <div className="hidden sm:block p-1 rounded-full bg-slate-100 group-hover:bg-white border border-transparent group-hover:border-slate-200 transition-all">
                        <ChevronRight
                          className={`h-4 w-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''
                            }`}
                        />
                      </div>
                    </div>
                  </button>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="px-6 pb-4 pl-14 animate-in slide-in-from-top-2 duration-200">
                      <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-slate-400 text-xs mb-0.5">Solde avant</p>
                            <p className="font-medium text-slate-700">
                              {formatCurrency(entry.balanceBefore)}
                            </p>
                          </div>
                          <div>
                            <p className="text-slate-400 text-xs mb-0.5">Solde après</p>
                            <p className="font-medium text-slate-700">
                              {formatCurrency(entry.balanceAfter)}
                            </p>
                          </div>
                          <div>
                            <p className="text-slate-400 text-xs mb-0.5">Variation</p>
                            <p
                              className={`font-medium ${isPositive ? 'text-amber-600' : 'text-emerald-600'
                                }`}
                            >
                              {formatDelta(entry.delta)}
                            </p>
                          </div>
                          {entry.productIndex !== undefined && (
                            <div>
                              <p className="text-slate-400 text-xs mb-0.5">Produit</p>
                              <p className="font-medium text-slate-700">#{entry.productIndex}</p>
                            </div>
                          )}
                        </div>

                        {/* References */}
                        {(entry.orderId ||
                          entry.paymentIntentId ||
                          entry.chargeId ||
                          entry.refundId) && (
                            <div className="pt-3 border-t border-slate-200">
                              <p className="text-xs text-slate-400 mb-2">Références</p>
                              <div className="flex flex-wrap gap-2">
                                {entry.orderId && (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-md text-xs font-mono text-slate-600 border border-slate-200">
                                    <Package className="h-3 w-3" />
                                    {entry.orderId}
                                  </span>
                                )}
                                {entry.paymentIntentId && (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-md text-xs font-mono text-slate-600 border border-slate-200">
                                    <CreditCard className="h-3 w-3" />
                                    {entry.paymentIntentId.slice(0, 20)}…
                                  </span>
                                )}
                                {entry.chargeId && (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-md text-xs font-mono text-slate-600 border border-slate-200">
                                    ch_{entry.chargeId.slice(-12)}
                                  </span>
                                )}
                                {entry.refundId && (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-md text-xs font-mono text-slate-600 border border-slate-200">
                                    <RefreshCw className="h-3 w-3" />
                                    {entry.refundId.slice(0, 16)}…
                                  </span>
                                )}
                              </div>
                            </div>
                          )}

                        {/* Note */}
                        {entry.note && (
                          <div className="pt-3 border-t border-slate-200">
                            <p className="text-xs text-slate-400 mb-1">Note</p>
                            <p className="text-sm text-slate-600 whitespace-pre-wrap">
                              {entry.note}
                            </p>
                          </div>
                        )}

                        {/* Metadata - nicely formatted */}
                        {entry.metadata && Object.keys(entry.metadata).length > 0 && (
                          <div className="pt-3 border-t border-slate-200">
                            <p className="text-xs text-slate-400 mb-2">Détails du calcul</p>
                            <div className="flex flex-wrap gap-3">
                              {/* Shipping comparison block */}
                              {(entry.metadata.shippingCustomPrice !== undefined ||
                                entry.metadata.shippingActualPrice !== undefined) && (
                                  <div className="bg-white rounded-lg border border-slate-100 p-3 flex-1 min-w-[200px]">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Package className="h-3.5 w-3.5 text-slate-400" />
                                      <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">
                                        Frais de port
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <div className="flex-1">
                                        <p className="text-[10px] text-slate-400">Facturé client</p>
                                        <p className="text-sm font-semibold text-slate-700">
                                          {formatCurrency(entry.metadata.shippingCustomPrice ?? 0)}
                                        </p>
                                      </div>
                                      <div className="text-slate-300">→</div>
                                      <div className="flex-1">
                                        <p className="text-[10px] text-slate-400">Coût réel</p>
                                        <p className="text-sm font-semibold text-slate-700">
                                          {formatCurrency(entry.metadata.shippingActualPrice ?? 0)}
                                        </p>
                                      </div>
                                      {entry.metadata.shippingDeficit !== undefined &&
                                        entry.metadata.shippingDeficit > 0 && (
                                          <div className="flex-1 pl-3 border-l border-slate-100">
                                            <p className="text-[10px] text-amber-500">Déficit</p>
                                            <p className="text-sm font-semibold text-amber-600">
                                              {formatCurrency(entry.metadata.shippingDeficit)}
                                            </p>
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                )}

                              {/* Other metadata */}
                              {Object.entries(entry.metadata)
                                .filter(
                                  ([key]) =>
                                    ![
                                      'shippingCustomPrice',
                                      'shippingActualPrice',
                                      'shippingDifference',
                                      'shippingDeficit',
                                      'applicationFeeAmount',
                                    ].includes(key)
                                )
                                .map(([key, value]) => {
                                  const label = metadataLabels[key] || key;
                                  const numValue = typeof value === 'number' ? value : null;
                                  const isWeightKey = /WeightGrams$/i.test(key) || key === 'parcelWeightDeltaGrams';

                                  return (
                                    <div
                                      key={key}
                                      className="bg-white rounded-lg p-3 border border-slate-100 min-w-[120px]"
                                    >
                                      <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">
                                        {label}
                                      </p>
                                      <p className="text-sm font-medium text-slate-700">
                                        {numValue !== null
                                          ? (isWeightKey ? formatKgFromGrams(numValue) : formatCurrency(numValue))
                                          : typeof value === 'boolean'
                                            ? value
                                              ? '✓ Oui'
                                              : '✗ Non'
                                            : String(value)}
                                      </p>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BalanceHistoryPage;
